/**
 * Manual box office and Rotten Tomatoes data for films in the Box Office Rail.
 * This data is used to augment films that don't have data from our theatrical releases API.
 *
 */

export interface ManualFilmData {
  /** US domestic box office (e.g., "$126.5M") */
  usBoxOffice: string | null
  /** Worldwide box office (e.g., "$320M") */
  worldBoxOffice: string | null
  /** Rotten Tomatoes critics score (0-100) - only for non-Angel Original films */
  rottenTomatoesCritics?: number | null
  /** Rotten Tomatoes audience score (0-100) - only for non-Angel Original films */
  rottenTomatoesAudience?: number | null
  /** Override release date if needed (YYYY-MM-DD format) */
  releaseDate?: string | null
}

/**
 * Manual film data keyed by project slug.
 * Add entries here to provide box office and RT data for films in the collection.
 */
export const manualFilmData: Record<string, ManualFilmData> = {
  // Example entries - update with real data
  homestead: {
    usBoxOffice: '$20.8M',
    worldBoxOffice: '$20.8M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'king-of-kings': {
    usBoxOffice: '$60.2M',
    worldBoxOffice: '$83.1M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'last-rodeo': {
    usBoxOffice: '$15.2M',
    worldBoxOffice: '$15.2M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'the-shift': {
    usBoxOffice: '$12.1M',
    worldBoxOffice: '$12.2M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'faith-of-angels': {
    usBoxOffice: '$570K',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: 94,
  },
  'truth-and-treason': {
    usBoxOffice: '$6M',
    worldBoxOffice: '$6M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'thank-you-dr-fauci': {
    usBoxOffice: 'N/A',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: 86,
  },
  'green-and-gold': {
    usBoxOffice: '$1.7M',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: 67,
    rottenTomatoesAudience: 98,
  },
  'sound-of-hope-the-story-of-possum-trot': {
    usBoxOffice: '$11.6M',
    worldBoxOffice: '$11.6M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'his-only-son': {
    usBoxOffice: '$12M',
    worldBoxOffice: '$13.5M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  sketch: {
    usBoxOffice: '$8.1M',
    worldBoxOffice: '$10.7M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'after-death': {
    usBoxOffice: '$11.4M',
    worldBoxOffice: '$11.8M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  senior: {
    usBoxOffice: '$5.2M',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'brave-the-dark': {
    usBoxOffice: '$4.5M',
    worldBoxOffice: '$4.5M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'sound-of-freedom': {
    usBoxOffice: '$184.1M',
    worldBoxOffice: '$250.7M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  bonhoeffer: {
    usBoxOffice: '$12.2M',
    worldBoxOffice: '$12.6M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  cabrini: {
    usBoxOffice: '$19.5M',
    worldBoxOffice: '$20.5M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  'lost-on-a-mountain-in-maine': {
    usBoxOffice: '$1.1M',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: 53,
    rottenTomatoesAudience: 82,
  },
  'raising-the-bar': {
    usBoxOffice: '$463K',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  grow: {
    usBoxOffice: 'N/A',
    worldBoxOffice: '$59K',
    rottenTomatoesCritics: 93,
    rottenTomatoesAudience: 97,
  },
  'something-to-stand-for-with-mike-rowe': {
    usBoxOffice: '$1.3M',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: 80,
    rottenTomatoesAudience: 98,
  },
  'escape-from-germany': {
    usBoxOffice: '$1.3M',
    worldBoxOffice: 'N/A',
    rottenTomatoesCritics: 80,
    rottenTomatoesAudience: 98,
  },
  'rule-breakers': {
    usBoxOffice: '$2.9M',
    worldBoxOffice: '$2.9M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
  sight: {
    usBoxOffice: '$7.2M',
    worldBoxOffice: '$7.2M',
    rottenTomatoesCritics: null,
    rottenTomatoesAudience: null,
  },
}

/**
 * Gets manual film data for a given slug, or undefined if not found
 */
export function getManualFilmData(slug: string): ManualFilmData | undefined {
  return manualFilmData[slug]
}
